import React, { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { MydawaLogo } from "@/components/MydawaLogo";
import { formatApiError } from "@/lib/api";
import { Lock, Mail, Loader2 } from "lucide-react";

const BG =
  "https://static.prod-images.emergentagent.com/jobs/f016b904-7317-4dc9-bed9-f4b71ecc7608/images/4458f48ce5474757c889fe64de0b2d3f806beff7486cfdcc4a1eb7dac2fd6c53.png";

export default function AdminLogin() {
  const { user, loading, login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("admin@mydawa.com");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  if (loading) return null;
  if (user && user.role) return <Navigate to="/admin" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setBusy(true);
    try {
      await login(email, password);
      nav("/admin", { replace: true });
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid min-h-screen bg-white md:grid-cols-2">
      <div className="relative hidden md:block">
        <img src={BG} alt="MYDAWA Admin" className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-br from-[#66cc33]/20 via-transparent to-[#ff3399]/20"></div>
        <div className="absolute inset-x-0 bottom-0 p-10 text-white">
          <div className="max-w-sm text-sm font-medium opacity-90">
            "Referral growth, industrialized. Run campaigns, manage promo codes and payouts — all from one console."
          </div>
        </div>
      </div>

      <div className="flex flex-col justify-center px-6 py-10 sm:px-16">
        <MydawaLogo />
        <div className="mt-10 max-w-sm">
          <h1 className="text-3xl font-extrabold tracking-tight text-[#1a1c19]">Admin Console</h1>
          <p className="mt-2 text-sm text-[#5c665a]">
            Sign in to manage campaigns, promo pools, and payouts for Refer & Earn.
          </p>
          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="admin-login-form">
            <div className="space-y-2">
              <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#5c665a]">Email</label>
              <div className="relative">
                <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#5c665a]" />
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="admin-input !pl-10 py-3"
                  data-testid="admin-email-input"
                />
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-xs font-bold uppercase tracking-[0.2em] text-[#5c665a]">Password</label>
              <div className="relative">
                <Lock className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#5c665a]" />
                <input
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="admin-input !pl-10 py-3"
                  data-testid="admin-password-input"
                />
              </div>
            </div>
            {err && (
              <div className="rounded-lg bg-[#fff0f5] px-4 py-2 text-sm font-medium text-[#c01974]" data-testid="admin-login-error">
                {err}
              </div>
            )}
            <button type="submit" disabled={busy} className="admin-btn w-full py-3 text-base" data-testid="admin-login-btn">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
            </button>
          </form>
          <div className="mt-6 rounded-lg border border-[#e2e8e2] bg-[#fafcfa] p-3 text-xs text-[#5c665a]">
            <strong>Demo credentials:</strong> admin@mydawa.com / Admin@2026
          </div>
        </div>
      </div>
    </div>
  );
}
